import { useEffect, useId } from 'react';
import type React from 'react';

import { createPortal } from 'react-dom';

import { Button, Text } from '@change/ds-core-ui';
import { CheckmarkVerifiedIcon } from '@change/ds-icons';

import { Translate, TranslatePlural, useI18n } from '@change-corgi/core-react/i18n';
import { ClientRender } from '@change-corgi/core-react/ssr/render';

import { useDecisionMakerOutreachStats } from '#src/app/shared/hooks/contactDecisionMaker';
import { useInertPageRoot } from '#src/app/shared/hooks/focus';
import { focus } from '#src/app/shared/utils/dom';

import type { ContactDecisionMakerSuccessStageProps } from '../types';

import { useCountUp } from './hooks/useCountUp';

/**
 * Screen 4 (CHANGE-97533). The confirmation after a message is accepted for delivery.
 *
 * Not a sheet — a full-bleed overlay, so the petition behind it is gone and this is the whole screen.
 * The share sheet layers on top of it rather than replacing it.
 *
 * It is still a dialog, and has to say so. `fixed inset-0` hides the petition visually but leaves it
 * reachable, so without `aria-modal` a screen reader reads straight through to the page underneath
 * and focus starts on `<body>`.
 *
 * `aria-modal` alone would keep assistive technology out of the background while leaving a sighted
 * keyboard user free to tab into it — and because completion is recorded the moment the send
 * succeeds, the impact section's "Invite others" button is already mounted behind this overlay.
 *
 * So it is portaled to `document.body` and the page root is marked `inert` while it is up. Trapping
 * focus locally was tried and rejected: it fights the share sheet's own trap when that layers on top.
 * `inert` has no such conflict — the share sheet portals out of the root as well, so it stays
 * reachable.
 */
export const Success: React.FC<ContactDecisionMakerSuccessStageProps> = ({
	decisionMaker,
	petitionId,
	flow,
	headingRef,
	isShareLoading,
}) => {
	const { translate, localizeNumber } = useI18n();
	const { contactedSupporterCount: reportedCount } = useDecisionMakerOutreachStats(petitionId);
	// The viewer just sent a message, so the total is at least one whatever the query says. The stub
	// resolver returns 0 today, and en-US CLDR has no `zero` form, so 0 selects `other` and the
	// sub-line reads "You're one of 0 supporters who've reached out" on every success.
	const contactedSupporterCount = reportedCount === undefined ? undefined : Math.max(1, reportedCount);
	const displayedCount = useCountUp(contactedSupporterCount);
	const headingId = useId();

	// Portaled out of the page root below, so this is what keeps the background out of the tab order.
	useInertPageRoot(true);

	// The overlay is `fixed` and rendered in place, so when its own content fits there is nothing to
	// scroll inside it and wheel/touch scroll reaches the petition behind the opaque overlay — the
	// page moves under the overlay and "Back to the petition" lands somewhere else. Locking the body
	// is the whole fix and needs nothing from the DS. The previous value is restored rather than
	// cleared: the share sheet layers over this screen and sets its own lock.
	useEffect(() => {
		const { body } = document;
		const previousOverflow = body.style.overflow;
		body.style.overflow = 'hidden';

		return () => {
			body.style.overflow = previousOverflow;
		};
	}, []);

	// The overlay replaces the page, and nothing here is auto-focused, so focus would sit on `<body>`
	// behind it. Moved to the heading rather than a button, so the first thing announced is what
	// happened. Focus returns to the entry control when the flow closes — the flow owns that.
	useEffect(() => {
		focus(headingRef.current, { makeFocusable: true });
	}, [headingRef]);

	return createPortal(
		// `open`, not `showModal()`: a modal dialog goes into the browser's top layer, which no z-index
		// can beat, and the share sheet has to render above this. `aria-modal` still keeps assistive
		// technology out of the background, which matches what the opaque overlay does visually.
		// `m-0 max-h-none max-w-none` undoes the UA dialog box; the rest is the overlay itself.
		<dialog
			open
			aria-modal="true"
			aria-labelledby={headingId}
			className="bg-background-base fixed inset-0 z-70 m-0 flex max-h-none w-full max-w-none flex-col items-center overflow-y-auto overscroll-contain px-7 pt-20 pb-6 text-center"
			data-testid="contact-decision-maker-success"
		>
			<Text
				as="h1"
				id={headingId}
				ref={headingRef}
				tabIndex={-1}
				className="text-size-heading-small text-typography-strong mt-6 max-w-[290px]"
			>
				<Translate value="corgi.contact_decision_maker.success.heading" />
			</Text>

			{/* Hidden until the count resolves rather than rendering a placeholder number: the sub-line's
				    whole job is to say how many people have done this, and a wrong figure undercuts it. */}
			{contactedSupporterCount !== undefined && (
				<Text className="text-size-body text-typography-weak mt-3 max-w-[300px]">
					{/* Intl output must not differ between SSR and hydration. */}
					<ClientRender>
						{/* `count` is the real total, not the animated value: pluralisation must not flip
							    mid-animation, which flashed the singular line for one tick on every total above one. */}
						<TranslatePlural
							value="corgi.contact_decision_maker.success.subline"
							count={contactedSupporterCount}
							replacements={{ supporterCount: localizeNumber(displayedCount ?? contactedSupporterCount) }}
						/>
					</ClientRender>
				</Text>
			)}

			{/* One promise, not the spec's three. The other two are held back on purpose — see the flow doc. */}
			<div className="border-stroke-weaker bg-fill-weaker rounded-medium mt-6 flex w-full items-center gap-3 border border-solid px-4 py-4 text-left">
				<CheckmarkVerifiedIcon size="small" className="text-icon-success shrink-0" />
				<Text className="text-size-body-small text-typography-strong">
					<Translate
						value="corgi.contact_decision_maker.success.promise_delivered"
						replacements={{ decision_maker_name: decisionMaker.displayName }}
					/>
				</Text>
			</div>

			<div className="mt-auto flex w-full flex-col gap-2 pt-8">
				{/* Waits for the share links rather than opening a sheet with nothing in it. */}
				<Button variant="primary" className="w-full" loading={isShareLoading} onClick={flow.actions.openShare}>
					{translate('corgi.contact_decision_maker.success.share_cta')}
				</Button>
				<Button variant="tertiary" className="w-full" onClick={flow.actions.finish}>
					{translate('corgi.contact_decision_maker.success.back_cta')}
				</Button>
			</div>
		</dialog>,
		document.body,
	);
};
